import React, { FocusEventHandler, forwardRef, JSX, MouseEventHandler, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { EoraSpace, IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { ITextAlignProps, useDefaultFontCss, useTextAlignCss } from '../../fundamentals/typography/typography';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { IInvalidProps, usePassedFocusCss } from '../../fundamentals/actions';
import { mergeRefs } from '../../common/utils';
import { IBorderRadiusProps } from '../../fundamentals/sizes/borderRadius';
import { IFullWidthProps, useFullWidthCss } from '../../fundamentals/breakpoints/breakpoints';
import { BaseTextArea, BaseDiv } from '../../common/BaseElements';
import { HelpText } from '../HelpText/HelpText';
import { Label } from '../Label/Label';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import {
  useTextAreaBorderCss,
  useTextAreaFontSizeCss,
  useTextAreaPaddingCss,
  useTextAreaPlaceholderCss,
  useTextAreaTextCss,
  useTextAreaWidthCss
} from './TextAreaStyles';

export type TextAreaSize = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;
export interface ITextAreaSizeProps {
  textAreaSize?: TextAreaSize;
}

export type TextAreaWidth = typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE | typeof SIZES.X_LARGE;
export interface ITextAreaWidthProps {
  width?: TextAreaWidth | number;
}

export interface ITextAreaProps
  extends Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, 'width'>,
    IGutterAsMarginProps,
    IInvalidProps,
    ITextAreaSizeProps,
    ITextAreaWidthProps,
    ITextAlignProps,
    IFullWidthProps {
  rows?: number;
  charLimit?: number;
  label?: string;
  helpText?: JSX.Element | JSX.Element[];
  helpTextAsError?: boolean;
}

const TextAreaPads = (eoraSpaces: Record<EoraSpace, PxToRem>): Record<TextAreaSize, string> => ({
  [SIZES.SMALL]: `${eoraSpaces.offgrid_6} ${eoraSpaces.medium}`,
  [SIZES.MEDIUM]: `${eoraSpaces.medium} ${eoraSpaces.offgrid_12}`,
  [SIZES.LARGE]: `${eoraSpaces.offgrid_10} ${eoraSpaces.large}`
});

export const DEFAULT_TEXT_AREA_SIZE = SIZES.MEDIUM;
export const DEFAULT_TEXT_AREA_WIDTH = SIZES.LARGE;

const useClearWebkitCss = css`
  &:-webkit-autofill::first-line,
  &:-webkit-autofill {
    ${useDefaultFontCss};
  }

  ::-ms-reveal {
    display: none;
  }

  /* Required css to remove spinner on Webkit browsers */
  &&::-webkit-outer-spin-button,
  &&::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

export const useTextAreaResetCss = css`
  appearance: none;
  color: inherit;
  border: none;
  background: transparent;
  width: inherit;
  padding: 0;

  &:focus {
    outline-style: none;
    box-shadow: none;
    border-color: transparent;
  }
  caret-color: ${({ theme }) => theme.colours.text.default};

  ${useClearWebkitCss}
`;

export type ITextAreaCssProps = Omit<ITextAreaProps, 'width'> & {
  styleWidth?: ITextAreaWidthProps['width'];
};

export const TextAreaBase = styled(BaseTextArea)<ITextAreaCssProps>`
  ${useTextAreaResetCss};
  ${useTextAreaFontSizeCss};
  ${useTextAlignCss};

  resize: none;
  width: 100%;

  &::placeholder {
    ${useTextAreaPlaceholderCss};
  }
`;

const TextAreaContainer = styled(BaseDiv)<ITextAreaCssProps & { focused?: boolean } & IBorderRadiusProps>`
  display: flex;
  flex-direction: column;
  ${useCenteredFlexCss};
  ${useGutterAsMarginCss()};
  ${useTextAreaPaddingCss};
  ${useTextAreaTextCss};

  padding: ${({ theme, textAreaSize }) => TextAreaPads(theme.eoraSpaces)[textAreaSize!]};
  background: ${({ theme, disabled }) => theme.textFieldColours[disabled ? `disabled` : `default`].background};
  border-radius: ${({ theme }) => theme.brandBorderRadius.input};

  ${useTextAreaWidthCss()};
  ${useTextAreaBorderCss};
  ${usePassedFocusCss};
  ${useFullWidthCss};
`;

const Container = styled(GapContainer)`
  display: flex;
  flex-direction: column;
`;

export const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    {
      rows = 4,
      charLimit = 200,
      label,
      required,
      textAreaSize = DEFAULT_TEXT_AREA_SIZE,
      disabled,
      className,
      style,
      fullWidth,
      width = DEFAULT_TEXT_AREA_WIDTH,
      helpText,
      helpTextAsError,
      onChange,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, 'Input');

    const [inputFocused, setInputFocused] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [count, setCount] = React.useState(charLimit);

    const handleOnInputFocus: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      rest.onFocus?.(e);
      setInputFocused(true);
    };

    const handleOnInputOnBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      rest.onBlur?.(e);
      setInputFocused(false);
    };

    const handleContainerOnClick: MouseEventHandler<HTMLTextAreaElement> = () => {
      inputRef?.current?.focus();
    };

    const { gutter, gutterBottom, gutterLeft, gutterRight, gutterTop, invalid } = rest;
    const containerProps = { disabled, gutter, gutterBottom, gutterLeft, gutterRight, gutterTop, invalid };

    return (
      <Container gap={'medium'}>
        <Label required={required}>{label}</Label>
        {!!helpText && (
          <HelpText textEoraSize='SMALL' textStyle='REGULAR' invalid={helpTextAsError} disabled={disabled} gutterBottom={false} {...generateTestId('HelpText')}>
            {helpText}
          </HelpText>
        )}
        <TextAreaContainer
          focused={inputFocused}
          textAreaSize={textAreaSize}
          {...generateTestId(`root`)}
          {...containerProps}
          className={className}
          style={style}
          fullWidth={fullWidth}
          styleWidth={width}
          onClick={handleContainerOnClick}
        >
          <TextAreaBase
            rows={rows}
            ref={mergeRefs([inputRef, ref])}
            textAreaSize={textAreaSize}
            disabled={disabled}
            {...generateTestId('textAreaBase')}
            {...rest}
            onFocus={handleOnInputFocus}
            onBlur={handleOnInputOnBlur}
            onChange={(e) => {
              setCount(charLimit - e.target.value.length);
              if (onChange) {
                onChange(e);
              }
            }}
          />
        </TextAreaContainer>
        <HelpText textEoraSize='SMALL' textStyle='REGULAR' invalid={count < 0} gutterBottom={false}>
          {count < 0 ? `Max. characters: ${charLimit}. ${-count} characters too many` : `You have ${count} characters remaining.`}
        </HelpText>
      </Container>
    );
  }
);
